import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Gift } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import LandingContainer from '../LandingContainer';
import SectionHeader from '../common/SectionHeader';
import {
  BASE_SUBMISSION_RATE,
  calculateMonthlyCost,
  ENDPOINT_BLOCK_PRICE,
  ENDPOINT_BLOCK_SIZE,
  formatCents,
  formatEuro,
  FREE_ENDPOINT_ALLOWANCE,
  FREE_SUBMISSION_ALLOWANCE,
  PRICING_PRESETS,
} from '../../../utils/pricingCalculator';

const SIMPLE_RULES = [
  {
    title: 'Start for €0',
    body: 'No credit card. No setup fee. No monthly minimum.',
  },
  {
    title: `${FREE_SUBMISSION_ALLOWANCE} free form submissions / month`,
    body: `A "form submission" is one person submitting a form. The first ${FREE_SUBMISSION_ALLOWANCE} each month cost nothing.`,
  },
  {
    title: `${FREE_ENDPOINT_ALLOWANCE} free forms / account`,
    body: `A "form" is one form on your site (contact, signup, etc.). The first ${FREE_ENDPOINT_ALLOWANCE} are free.`,
  },
  {
    title: 'Pay only when you go over',
    body: `Extra submissions cost ${formatCents(BASE_SUBMISSION_RATE)} each. More than ${FREE_ENDPOINT_ALLOWANCE} forms costs €${ENDPOINT_BLOCK_PRICE}/month for up to ${ENDPOINT_BLOCK_SIZE} forms.`,
  },
];

const CalculatorField = ({ label, help, value, onChange, min, max, step = 1 }) => {
  const clamp = (next) => Math.min(max, Math.max(min, next));

  return (
    <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
      <div className="mb-1 flex items-center justify-between gap-3">
        <label className="text-sm font-semibold text-charcoal-900">{label}</label>
        <Input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(event) => onChange(clamp(Number(event.target.value) || 0))}
          className="h-10 w-28 border-neutral-300 bg-white text-right"
          aria-label={label}
        />
      </div>
      <p className="mb-4 text-xs leading-5 text-charcoal-500">{help}</p>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(clamp(Number(event.target.value)))}
        className="h-2 w-full cursor-pointer accent-primary"
        aria-label={`${label} slider`}
      />
    </div>
  );
};

const ReceiptRow = ({ label, math, amount, highlight = false }) => (
  <div
    className={`flex items-start justify-between gap-4 py-2.5 ${
      highlight ? 'rounded-xl bg-success-50 px-3 -mx-3' : ''
    }`}
  >
    <div className="min-w-0">
      <p className="text-sm text-charcoal-800">{label}</p>
      {math && <p className="mt-0.5 font-mono text-xs text-charcoal-500">{math}</p>}
    </div>
    <span className="shrink-0 text-sm font-semibold text-charcoal-900">{amount}</span>
  </div>
);

const PricingSection = () => {
  const [submissions, setSubmissions] = useState(400);
  const [endpoints, setEndpoints] = useState(8);
  const [showEstimate, setShowEstimate] = useState(false);

  const cost = useMemo(
    () => calculateMonthlyCost(submissions, endpoints),
    [submissions, endpoints]
  );

  const submissionRows = useMemo(() => {
    if (cost.withinFreeSubmissions) {
      return [
        {
          label: `You use ${cost.submissions} form submissions — all inside the free ${FREE_SUBMISSION_ALLOWANCE}`,
          amount: '€0.00',
          highlight: true,
        },
      ];
    }

    const rows = [
      {
        label: `First ${FREE_SUBMISSION_ALLOWANCE} form submissions (free every month)`,
        amount: '€0.00',
      },
      {
        label: `${cost.billedSubmissions} extra submissions`,
        math: `${cost.billedSubmissions} × ${formatEuro(cost.effectiveRate)} = ${formatEuro(cost.submissionCost)}`,
        amount: formatEuro(cost.submissionCost),
      },
    ];

    if (cost.volumeDiscountLabel) {
      rows.push({
        label: `Good news: ${cost.volumeDiscountLabel} — each extra submission costs ${formatCents(cost.effectiveRate)} instead of ${formatCents(BASE_SUBMISSION_RATE)}`,
        amount: '',
      });
    }

    return rows;
  }, [cost]);

  const endpointRows = useMemo(() => {
    if (cost.withinFreeEndpoints) {
      return [
        {
          label: `You use ${cost.endpoints} forms — all inside the free ${FREE_ENDPOINT_ALLOWANCE}`,
          amount: '€0.00',
          highlight: true,
        },
      ];
    }

    return [
      {
        label: `First ${FREE_ENDPOINT_ALLOWANCE} forms (free on every account)`,
        amount: '€0.00',
      },
      {
        label: `${cost.endpoints} forms needs ${cost.paidBlocks} pack${cost.paidBlocks === 1 ? '' : 's'} of ${ENDPOINT_BLOCK_SIZE}`,
        math: `${cost.paidBlocks} × €${ENDPOINT_BLOCK_PRICE} = ${formatEuro(cost.endpointCost)}`,
        amount: formatEuro(cost.endpointCost),
      },
    ];
  }, [cost]);

  return (
    <section className="bg-neutral-50 py-12 xs:py-14 sm:py-24">
      <LandingContainer>
        <SectionHeader
          eyebrow="Pricing"
          title="How much does FormCare cost?"
          description="Free to start. You only pay if you get more form submissions or need more forms than the free allowance."
          className="mb-10 sm:mb-12"
        />

        <div className="mx-auto max-w-2xl lg:max-w-6xl">
          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:mb-6 lg:grid-cols-4">
            {SIMPLE_RULES.map((rule) => (
              <div
                key={rule.title}
                className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5"
              >
                <div className="flex items-start gap-3 lg:flex-col lg:gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-success lg:mt-0" aria-hidden />
                  <div>
                    <p className="text-sm font-semibold text-charcoal-900">{rule.title}</p>
                    <p className="mt-1 text-sm leading-6 text-charcoal-600">{rule.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!showEstimate ? (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setShowEstimate(true)}
                className="inline-flex min-h-11 items-center text-sm font-semibold text-primary-700 hover:text-primary-800"
              >
                Estimate cost above {FREE_SUBMISSION_ALLOWANCE} submissions
              </button>
            </div>
          ) : (
          <div className="mt-6 grid gap-4 lg:grid-cols-2 lg:items-stretch">
          <div className="flex h-full flex-col rounded-[2rem] border border-neutral-200 bg-white p-5 shadow-sm xs:p-6 sm:p-8">
            <div className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-primary" aria-hidden />
              <h3 className="font-heading text-lg font-bold text-charcoal-900">Try your own numbers</h3>
            </div>
            <p className="mt-2 text-sm leading-6 text-charcoal-600">
              Move the sliders or pick an example. We show the math step by step — no surprises.
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              {PRICING_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => {
                    setSubmissions(preset.submissions);
                    setEndpoints(preset.endpoints);
                  }}
                  className="rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-xs font-semibold text-charcoal-700 transition-colors hover:border-primary-200 hover:bg-primary-50 hover:text-primary-800"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="mt-6 flex flex-1 flex-col justify-center gap-4">
              <CalculatorField
                label="How many form submissions per month?"
                help="Count every time someone hits submit — contact forms, signups, uploads, all of them."
                value={submissions}
                onChange={setSubmissions}
                min={0}
                max={30000}
                step={50}
              />
              <CalculatorField
                label="How many forms on your site?"
                help="One contact page = 1 form. A signup form and a support form = 2 forms."
                value={endpoints}
                onChange={setEndpoints}
                min={0}
                max={500}
                step={1}
              />
            </div>
          </div>

          <div className="flex h-full flex-col rounded-[2rem] border border-neutral-200 bg-white p-5 shadow-sm xs:p-6 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-charcoal-500">Your monthly bill</p>

            <div
              className={`mt-4 rounded-2xl px-4 py-5 text-center ${
                cost.totalCost === 0 ? 'bg-success-50' : 'bg-charcoal-900'
              }`}
            >
              <p className={`text-sm font-medium ${cost.totalCost === 0 ? 'text-success-800' : 'text-neutral-300'}`}>
                {cost.totalCost === 0 ? 'You pay nothing this month' : 'You would pay'}
              </p>
              <p
                className={`mt-1 font-heading text-4xl font-bold sm:text-5xl ${
                  cost.totalCost === 0 ? 'text-success-900' : 'text-white'
                }`}
              >
                {formatEuro(cost.totalCost)}
              </p>
              <p className={`mt-1 text-sm ${cost.totalCost === 0 ? 'text-success-700' : 'text-neutral-400'}`}>
                per month
              </p>
            </div>

            <div className="mt-6 flex flex-1 flex-col">
              <p className="mb-2 text-sm font-semibold text-charcoal-900">Form submissions</p>
              <div className="divide-y divide-neutral-100">
                {submissionRows.map((row) => (
                  <ReceiptRow key={row.label} {...row} />
                ))}
              </div>

              <div className="mt-6">
                <p className="mb-2 text-sm font-semibold text-charcoal-900">Forms on your site</p>
                <div className="divide-y divide-neutral-100">
                  {endpointRows.map((row) => (
                    <ReceiptRow key={row.label} {...row} />
                  ))}
                </div>
              </div>

              <div className="mt-auto flex items-center justify-between border-t border-neutral-200 pt-4">
                <span className="text-sm font-semibold text-charcoal-900">Total</span>
                <span className="font-heading text-xl font-bold text-charcoal-900">{formatEuro(cost.totalCost)} / month</span>
              </div>
            </div>

            <p className="mt-4 text-center text-xs leading-5 text-charcoal-500">
              No hidden fees. Bigger sites automatically get cheaper per-submission rates above 1,000 / 5,000 / 25,000 submissions.
            </p>
          </div>
          </div>
          )}

          <div className="mt-10 flex flex-col items-center gap-4 text-center">
            <p className="max-w-md text-sm leading-6 text-charcoal-600 sm:text-base">
              Still €0 to sign up. Add a card only when you go past the free allowance.
            </p>
            <Button asChild size="lg">
              <Link to="/register">
                Start free
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </LandingContainer>
    </section>
  );
};

export default PricingSection;
